import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu } from '@mui/material';
import { MouseEvent, ReactNode, useState } from 'react';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { DeleteMenuItem } from './DeleteMenuItem';

interface DeleteOverflowActionsProps {
  readonly onDelete: () => Promise<void>;
  readonly title?: string;
  readonly message: ReactNode;
  readonly successMessage?: ReactNode;
  readonly disabled?: boolean;
  readonly redirectTo?: string;
}

/**
 * Overflow menu (three dots) that hosts delete behind a confirmation dialog.
 * The dialog is rendered outside the Menu so closing the menu does not dismiss it.
 */
export function DeleteOverflowActions({
  onDelete,
  title,
  message,
  successMessage,
  disabled = false,
  redirectTo,
}: DeleteOverflowActionsProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const menuOpen = Boolean(menuAnchor);

  function handleOpenMenu(event: MouseEvent<HTMLElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function handleCloseMenu() {
    setMenuAnchor(null);
  }

  function handleRequestDelete() {
    handleCloseMenu();
    setDeleteDialogOpen(true);
  }

  return (
    <>
      <IconButton size="small" color="secondary" onClick={handleOpenMenu} disabled={disabled}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={menuOpen} onClose={handleCloseMenu}>
        <DeleteMenuItem onAction={handleRequestDelete} disabled={disabled} />
      </Menu>
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={onDelete}
        title={title}
        message={message}
        successMessage={successMessage}
        redirectTo={redirectTo}
      />
    </>
  );
}
